// Carrier library hooks — FE-SCRM-030.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createCarrier,
  createCarrierContact,
  deleteCarrier,
  deleteCarrierContact,
  fetchCarrierContacts,
  fetchCarrierDetail,
  fetchCarrierList,
  fetchCarrierTouchpoints,
  updateCarrier,
  updateCarrierContact,
} from "./api";
import {
  CarrierNotFoundError,
  type CarrierContact,
  type CarrierContactListResponse,
  type CarrierContactWriteInput,
  type CarrierDetail,
  type CarrierListFilters,
  type CarrierListResponse,
  type CarrierTouchpointListResponse,
  type CarrierWriteInput,
} from "./types";


export const CARRIERS_QUERY_KEY = "carriers" as const;
export const CARRIER_CONTACTS_QUERY_KEY = "carrier-contacts" as const;
export const CARRIER_TOUCHPOINTS_QUERY_KEY = "carrier-touchpoints" as const;


export function useCarrierListQuery(
  filters: CarrierListFilters,
): UseQueryResult<CarrierListResponse> {
  return useQuery({
    queryKey: [CARRIERS_QUERY_KEY, "list", filters],
    queryFn: ({ signal }) => fetchCarrierList(filters, { signal }),
    placeholderData: (prev) => prev,
  });
}


export function useCarrierDetailQuery(
  carrierId: string | undefined,
): UseQueryResult<CarrierDetail> {
  return useQuery({
    queryKey: [CARRIERS_QUERY_KEY, "detail", carrierId],
    queryFn: ({ signal }) => fetchCarrierDetail(carrierId!, { signal }),
    enabled: Boolean(carrierId),
    retry: (failureCount, error) =>
      !(error instanceof CarrierNotFoundError) && failureCount < 1,
  });
}


export function useCarrierContactsQuery(
  carrierId: string | undefined,
): UseQueryResult<CarrierContactListResponse> {
  return useQuery({
    queryKey: [CARRIER_CONTACTS_QUERY_KEY, carrierId],
    queryFn: ({ signal }) => fetchCarrierContacts(carrierId!, { signal }),
    enabled: Boolean(carrierId),
  });
}


export function useCarrierTouchpointsQuery(
  carrierId: string | undefined,
): UseQueryResult<CarrierTouchpointListResponse> {
  return useQuery({
    queryKey: [CARRIER_TOUCHPOINTS_QUERY_KEY, carrierId],
    queryFn: ({ signal }) => fetchCarrierTouchpoints(carrierId!, { signal }),
    enabled: Boolean(carrierId),
  });
}


function _invalidateCarrierWritePaths(
  qc: ReturnType<typeof useQueryClient>, carrierId?: string,
) {
  qc.invalidateQueries({ queryKey: [CARRIERS_QUERY_KEY, "list"] });
  if (carrierId) {
    qc.invalidateQueries({
      queryKey: [CARRIERS_QUERY_KEY, "detail", carrierId],
    });
  }
}


export function useCreateCarrierMutation(): UseMutationResult<
  CarrierDetail, Error, CarrierWriteInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CarrierWriteInput) => createCarrier(input),
    onSuccess: () => _invalidateCarrierWritePaths(qc),
  });
}


export interface UpdateCarrierVariables {
  carrier_id: string;
  values: Partial<CarrierWriteInput>;
}


export function useUpdateCarrierMutation(): UseMutationResult<
  CarrierDetail, Error, UpdateCarrierVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => updateCarrier(vars.carrier_id, vars.values),
    onSuccess: (_data, vars) =>
      _invalidateCarrierWritePaths(qc, vars.carrier_id),
  });
}


export function useDeleteCarrierMutation(): UseMutationResult<
  { deleted: boolean }, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (carrierId: string) => deleteCarrier(carrierId),
    onSuccess: (_data, carrierId) =>
      _invalidateCarrierWritePaths(qc, carrierId),
  });
}


function _invalidateContactPaths(
  qc: ReturnType<typeof useQueryClient>, carrierId: string,
) {
  qc.invalidateQueries({
    queryKey: [CARRIER_CONTACTS_QUERY_KEY, carrierId],
  });
  // Bust the list-page cache too so contact_count aggregates refresh.
  qc.invalidateQueries({ queryKey: [CARRIERS_QUERY_KEY, "list"] });
  qc.invalidateQueries({
    queryKey: [CARRIERS_QUERY_KEY, "detail", carrierId],
  });
}


export function useCreateCarrierContactMutation(
  carrierId: string,
): UseMutationResult<CarrierContact, Error, CarrierContactWriteInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => createCarrierContact(carrierId, input),
    onSuccess: () => _invalidateContactPaths(qc, carrierId),
  });
}


export interface UpdateCarrierContactVariables {
  contact_id: string;
  values: Partial<CarrierContactWriteInput>;
}


export function useUpdateCarrierContactMutation(
  carrierId: string,
): UseMutationResult<
  CarrierContact, Error, UpdateCarrierContactVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      updateCarrierContact(vars.contact_id, vars.values),
    onSuccess: () => _invalidateContactPaths(qc, carrierId),
  });
}


export function useDeleteCarrierContactMutation(
  carrierId: string,
): UseMutationResult<{ deleted: boolean }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => deleteCarrierContact(contactId),
    onSuccess: () => _invalidateContactPaths(qc, carrierId),
  });
}
